import React, { useEffect, useState } from 'react';
import { Nav, Hero, Why, Features, Games, Devices, Pricing, CTAStrip, Footer } from './components/Sections';
import TweaksPanel, { useTweaks, TweakSection, TweakColor, TweakRadio } from './components/TweaksPanel';
import './styles.css';

const TWEAK_DEFAULTS = {
  accent: '#9A56FF',
  background: 'minimal',
  density: 'compact',
  billing: 'yearly',
};

// Helper functions for color manipulation
function hexToRgba(hex, a) {
  const h = hex.replace('#', '');
  const v = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const r = parseInt(v.slice(0, 2), 16);
  const g = parseInt(v.slice(2, 4), 16);
  const b = parseInt(v.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

function shade(hex, percent) {
  const h = hex.replace('#', '');
  const v = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  let r = parseInt(v.slice(0, 2), 16);
  let g = parseInt(v.slice(2, 4), 16);
  let b = parseInt(v.slice(4, 6), 16);
  r = Math.min(255, Math.round(r + (255 - r) * (percent / 100)));
  g = Math.min(255, Math.round(g + (255 - g) * (percent / 100)));
  b = Math.min(255, Math.round(b + (255 - b) * (percent / 100)));
  return `#${[r, g, b].map((n) => n.toString(16).padStart(2, '0')).join('')}`;
}

function App() {
  const [tweaks, setTweak] = useTweaks(TWEAK_DEFAULTS);
  const [tweaksOpen, setTweaksOpen] = useState(false);

  // Apply accent + density to root
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--accent', tweaks.accent);
    // derive accent-2 (lighter) and glow from accent
    root.style.setProperty('--accent-2', shade(tweaks.accent, 18));
    root.style.setProperty('--accent-glow', hexToRgba(tweaks.accent, 0.45));
    document.body.dataset.density = tweaks.density;
  }, [tweaks.accent, tweaks.density]);

  // Handle keyboard shortcut for TweaksPanel (Cmd/Ctrl + Shift + T)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'T') {
        e.preventDefault();
        setTweaksOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <>
      <Nav />
      <main>
        <Hero bgStyle={tweaks.background} />
        <Why />
        <Features />
        <Games />
        <Devices />
        <Pricing defaultBilling={tweaks.billing} key={tweaks.billing} />
        <CTAStrip />
      </main>
      <Footer />

      {tweaksOpen && (
        <TweaksPanel title="Tweaks">
          <TweakSection title="Brand">
            <TweakColor
              label="Accent"
              value={tweaks.accent}
              onChange={(v) => setTweak('accent', v)}
            />
          </TweakSection>
          <TweakSection title="Hero background">
            <TweakRadio
              value={tweaks.background}
              onChange={(v) => setTweak('background', v)}
              options={[
                { value: 'orb', label: 'Orb' },
                { value: 'particles', label: 'Particles' },
                { value: 'minimal', label: 'Minimal' },
              ]}
            />
          </TweakSection>
          <TweakSection title="Density">
            <TweakRadio
              value={tweaks.density}
              onChange={(v) => setTweak('density', v)}
              options={[
                { value: 'comfy', label: 'Comfy' },
                { value: 'compact', label: 'Compact' },
              ]}
            />
          </TweakSection>
          <TweakSection title="Default billing">
            <TweakRadio
              value={tweaks.billing}
              onChange={(v) => setTweak('billing', v)}
              options={[
                { value: 'monthly', label: 'Monthly' },
                { value: 'yearly', label: 'Yearly' },
              ]}
            />
          </TweakSection>
        </TweaksPanel>
      )}

      {/* Floating toggle button for Tweaks Panel */}
      <button
        onClick={() => setTweaksOpen(!tweaksOpen)}
        style={{
          position: 'fixed',
          right: 16,
          bottom: 16,
          width: 44,
          height: 44,
          borderRadius: 12,
          background: tweaksOpen ? 'var(--accent)' : 'rgba(255,255,255,0.08)',
          border: '1px solid var(--line-strong)',
          color: tweaksOpen ? '#fff' : 'var(--text-2)',
          display: 'grid',
          placeItems: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          zIndex: 1000,
        }}
        onMouseEnter={(e) => {
          if (!tweaksOpen) {
            e.currentTarget.style.background = 'rgba(255,255,255,0.12)';
            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)';
          }
        }}
        onMouseLeave={(e) => {
          if (!tweaksOpen) {
            e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
            e.currentTarget.style.borderColor = 'var(--line-strong)';
          }
        }}
        title="Toggle Tweaks Panel (Cmd+Shift+T)"
      >
        <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="3" />
          <path d="M12 1v6m0 6v10M1 12h6m10 0h10M4.2 4.2l4.2 4.2m7.2 0l4.2 4.2M4.2 19.8l4.2-4.2m7.2 0l4.2-4.2" />
        </svg>
      </button>
    </>
  );
}

export default App;
